import { FormEvent, useCallback, useEffect, useState } from "react";
import { UserCog } from "lucide-react";
import {
  buildResolvedOrgRolePermissions,
  getEditableRoles,
  OrgRolePermissionsSection,
  type AppId,
  type EnabledOrgRoles,
  type OrgRoleName,
  type OrgRolePermissions,
} from "@doculegis/shared";
import { GlassCard } from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { getOrgSettings, updateOrgSettings } from "@/lib/auth-client";

export function OrgRolesPage() {
  const { activeOrganization, syncSession } = useAuth();
  const [enabledOrgRoles, setEnabledOrgRoles] = useState<EnabledOrgRoles | null>(null);
  const [enabledApps, setEnabledApps] = useState<AppId[]>([]);
  const [rolePermissions, setRolePermissions] = useState<OrgRolePermissions>({});
  const [selectedRoleKey, setSelectedRoleKey] = useState<OrgRoleName | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getOrgSettings();
      setEnabledOrgRoles(data.enabledOrgRoles);
      setEnabledApps(data.enabledApps ?? []);
      setRolePermissions(data.orgRolePermissions ?? {});
      const editable = getEditableRoles(data.enabledOrgRoles);
      setSelectedRoleKey((current) =>
        current && editable.includes(current) ? current : editable[0] ?? null
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load organization roles");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!enabledOrgRoles) return;
    setError(null);
    setMessage(null);
    setIsSubmitting(true);

    try {
      const payload = buildResolvedOrgRolePermissions(rolePermissions, enabledOrgRoles);
      const data = await updateOrgSettings({ orgRolePermissions: payload });
      setRolePermissions(data.orgRolePermissions ?? payload);
      if (data.enabledApps) {
        setEnabledApps(data.enabledApps);
      }
      await syncSession();
      setMessage("Organization role permissions saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save organization roles");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="p-6 md:p-8">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] text-[#7c3aed] sm:h-10 sm:w-10 dark:border-violet-300/30 dark:bg-violet-500/15 dark:text-violet-200">
          <UserCog className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">Roles</h1>
          <p className="text-sm text-[var(--lm-muted)] dark:text-slate-300/85">
            Configure permissions for each membership role in{" "}
            {activeOrganization?.name ?? "your organization"}. Role availability is set by the
            system administrator.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/15 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      ) : null}

      {message ? (
        <div className="mb-4 rounded-lg border border-violet-400/25 bg-violet-500/10 px-4 py-3 text-sm text-violet-200">
          {message}
        </div>
      ) : null}

      {isLoading || !enabledOrgRoles ? (
        <p className="text-sm text-slate-400">Loading organization roles…</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <GlassCard className="border-[var(--lm-border-card)] bg-white shadow-[var(--shadow-card)] dark:border-indigo-300/25 dark:bg-slate-950/45 dark:shadow-none">
            <OrgRolePermissionsSection
              enabledOrgRoles={enabledOrgRoles}
              enabledApps={enabledApps}
              rolePermissions={rolePermissions}
              selectedRoleKey={selectedRoleKey}
              onSelectRoleKey={setSelectedRoleKey}
              onChange={setRolePermissions}
            />
          </GlassCard>

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving…" : "Save settings"}
          </Button>
        </form>
      )}
    </section>
  );
}
